import React, { useEffect, useMemo, useState } from "react";
import Images from "../../../../../Images";
import { Flex, Space, Spin, Tooltip, Typography } from "antd";
import ButtonComponent from "../../../../../Components/Button";
import cls from "classnames";
import Icon from "../../../../../Icon";
import { t } from "i18next";
import "../../../VAMP/components/MainChartBlock/MainChartBlock.less";
import MCChart from "../MCChart/MCChart";
import FAQModal from "../../../../../Components/Modal/FAQModal";
import { mcFAQ } from "./mcFAQ";
import SectionHeader from "../../../VAMP/components/SectionHeader/SectionHeader";
import ConnectStripePlaceholder from "../ConnectStripePlaceholder/ConnectStripePlaceholder";
import { useDispatch, useSelector } from "react-redux";
import HealthActions from "../../../reducer";
import { getDates } from "../../../../../Utils/utils";
import { getCompareParams } from "../../../../../Components/Filters/CompareSelect/presets";
import InfoLine from "../../../VAMP/components/InfoLine/InfoLine";
import NumberFlow from "@number-flow/react";
import { useAnimatedNumber } from "../../../../../hooks/useAnimatedNumber";
import PermissionPlaceholder from "../../../components/PermissionPlaceholder";
import useHealthAccessRestricted from "../../../hooks/useHealthAccessRestricted";
import { useDemoDataToggle } from "../../../../User/hooks/useDemoDataToggle";
import { useIsStripeConnected } from "../../hooks/useIsStripeConnected";
import { formatNumber } from "accounting";

const { Text } = Typography;

const REGION_UNAVAILABLE_TOOLTIP =
  "Region data unavailable. Value will update after next sync.";

const totalBlocksStyles = {
  chargeback: {
    title: "Chargeback Rate",
    titleCount: "Chargeback Count",
    color: "#225AEA",
  },
  fraud: {
    title: "Fraud Rate",
    titleCount: "Fraud Count",
    color: "#DF7009",
  },
  three_ds: {
    title: "3DS Usage",
    titleCount: "3DS Payment Count",
    color: "#35BD29",
  },
  ecm_zone: {
    title: "ECM Zone",
    color: "#8C6FF7",
    isDashed: true,
  },
  hecm_zone: {
    title: "HECM Zone",
    color: "#4A4A4A",
    isDashed: true,
  },
  efm_zone: {
    title: "EFM Zone",
    color: "#D71313",
    isDashed: true,
  },
};

const MCChartBlock = ({ filters, business_account_id }) => {
  const dispatch = useDispatch();
  const { isShowDemoData } = useDemoDataToggle();
  const { mcMainChart, mcMainChartFetching } = useSelector(
    (state) => state.health
  );
  const { isMobile } = useSelector((state) => state.nav);
  const [dataType, setDataType] = useState("rate");
  const [isFAQOpen, setIsFAQOpen] = useState(false);
  const isRestricted = useHealthAccessRestricted();
  const isStripeConnected = useIsStripeConnected();

  useEffect(() => {
    if (filters?.period && isStripeConnected) {
      const period_query = getDates(filters.period);
      const compare_query = getCompareParams(filters?.compare);
      dispatch(
        HealthActions.getMcMainChartRequest({
          ...period_query,
          ...compare_query,
          business_account: business_account_id,
        })
      );
    }
  }, [filters, business_account_id, isShowDemoData, isStripeConnected]);

  const totalBlocks = useMemo(() => {
    const isRate = dataType === "rate";

    if (!isStripeConnected) {
      const keys = isRate
        ? ["chargeback", "fraud", "three_ds", "ecm_zone", "hecm_zone", "efm_zone"]
        : ["chargeback", "fraud", "three_ds", "ecm_zone", "hecm_zone"];
      return keys.map((key) => ({
        value: null,
        title: isRate ? totalBlocksStyles[key].title : totalBlocksStyles[key].titleCount ?? totalBlocksStyles[key].title,
        color: totalBlocksStyles[key].color,
        isDashed: totalBlocksStyles[key].isDashed,
      }));
    }

    const blocks = mcMainChart?.blocks_data;
    const thresholds = mcMainChart?.thresholds;
    if (!blocks || !thresholds) return [];

    const compBlocks = mcMainChart?.comparison?.blocks_data;
    const compLabel = mcMainChart?.comparison_period?.label;
    const style = (key) => totalBlocksStyles[key];
    const withStyle = (key, value, comparisonValue, tooltip) => ({
      value,
      comparisonValue,
      comparisonLabel: comparisonValue != null ? compLabel : undefined,
      tooltip,
      title: isRate ? style(key).title : style(key).titleCount ?? style(key).title,
      color: style(key).color,
      isDashed: style(key).isDashed,
    });

    const items = [
      withStyle(
        "chargeback",
        isRate ? blocks.chargeback_rate : blocks.chargeback_count,
        isRate ? compBlocks?.chargeback_rate : compBlocks?.chargeback_count
      ),
      withStyle(
        "fraud",
        isRate ? blocks.fraud_rate : blocks.fraud_count,
        isRate ? compBlocks?.fraud_rate : compBlocks?.fraud_count
      ),
      withStyle(
        "three_ds",
        isRate ? blocks.three_ds_usage_rate : blocks.three_ds_payment_count,
        isRate ? compBlocks?.three_ds_usage_rate : compBlocks?.three_ds_payment_count
      ),
      withStyle("ecm_zone", isRate ? thresholds.ecm?.rate : thresholds.ecm?.count),
      withStyle("hecm_zone", isRate ? thresholds.hecm?.rate : thresholds.hecm?.count),
    ];

    if (isRate) {
      const efmRate = thresholds.efm?.rate;
      items.push(
        withStyle("efm_zone", efmRate, undefined, efmRate == null ? REGION_UNAVAILABLE_TOOLTIP : undefined)
      );
    }

    return items;
  }, [mcMainChart, dataType, isStripeConnected]);

  const ecmRate = mcMainChart?.thresholds?.ecm?.rate;
  const efmRate = mcMainChart?.thresholds?.efm?.rate;
  const infoLineText = `For the best health of your business — keep your Chargeback Rate below the ${
    ecmRate != null ? `${ecmRate}%` : "-"
  } ECM line and your Fraud Rate below the ${
    efmRate != null ? `${Number(efmRate).toFixed(2)}%` : "-"
  } EFM line.`;

  return (
    <div
      className={cls("health-monitor-main-chart-block", {
        "is-mobile": isMobile,
      })}
    >
      <Spin spinning={mcMainChartFetching}>
        <SectionHeader
          title="Mastercard Monitoring Programs (ECP & EFM)"
          image={Images.mastercard_3d}
          marginBottom={16}
        />
        <Flex
          gap={20}
          align="flex-start"
          justify="space-between"
          className="main-chart-legend"
          wrap="wrap-reverse"
        >
          <Flex gap={36} wrap="wrap" className="main-chart-legend__items">
            {totalBlocks.map((block) => (
              <TotalBlock
                key={block.title}
                block={block}
                dataType={dataType}
                isRestricted={isRestricted}
              />
            ))}
          </Flex>
          <Flex gap={8} className="main-chart-legend__controls">
            <ButtonComponent
              size={"large"}
              icon={<Icon type={"question_help"} />}
              onClick={() => setIsFAQOpen(true)}
            >
              What is ECP & EFM?
            </ButtonComponent>
            <Space.Compact size={"large"}>
              <ButtonComponent
                className={cls("compact-btn", {
                  active: dataType === "rate",
                })}
                style={{
                  borderRadius: "12px 0 0 12px",
                  minWidth: 66,
                }}
                disabled={isRestricted}
                onClick={() => setDataType("rate")}
              >
                {t("Rate")}
              </ButtonComponent>
              <ButtonComponent
                className={cls("compact-btn", {
                  active: dataType === "count",
                })}
                style={{
                  minWidth: 66,
                }}
                disabled={isRestricted}
                onClick={() => setDataType("count")}
              >
                {t("Count")}
              </ButtonComponent>
            </Space.Compact>
          </Flex>
        </Flex>
        {isRestricted ? (
          <PermissionPlaceholder minHeight={300} />
        ) : (
          <div
            className={cls("mc-stripe-gated-content", {
              "is-stripe-disconnected": !isStripeConnected,
            })}
            style={!isStripeConnected ? { minHeight: 340 } : undefined}
          >
            {isStripeConnected && <MCChart data={mcMainChart} dataType={dataType} />}
            <InfoLine
              text={infoLineText}
              variant="neutral"
              style={{ marginTop: "16px" }}
            />
            {!isStripeConnected && <ConnectStripePlaceholder />}
          </div>
        )}
      </Spin>
      <FAQModal
        open={isFAQOpen}
        close={() => setIsFAQOpen(false)}
        data={mcFAQ}
        title={"Learn more about Mastercard's ECP & EFM monitoring programs"}
      />
    </div>
  );
};

const TotalBlock = ({ block, dataType, isRestricted }) => {
  const animatedValue = useAnimatedNumber(block.value);
  const valueNode = isRestricted ? (
    "-"
  ) : (
    <>
      {block.value || block.value === 0 ? (
        <NumberFlow
          value={animatedValue}
          suffix={dataType === "rate" ? "%" : ""}
          format={
            dataType === "rate"
              ? { minimumFractionDigits: 2, maximumFractionDigits: 2 }
              : { maximumFractionDigits: 0 }
          }
        />
      ) : (
        "-"
      )}
    </>
  );

  return (
    <Flex gap={8} align="flex-start" className="chart-legend">
      <div className={cls(`legend-color`, { dashed: block.isDashed })}>
        <div
          className={cls(`color`, { dashed: block.isDashed })}
          style={{
            background: block.isDashed
              ? `repeating-linear-gradient(to right, ${block.color} 0 4px, transparent 4px 8px)`
              : block.color,
          }}
        />
      </div>
      <Flex gap={4} vertical>
        <Text type="secondary" strong>
          {block.title}
        </Text>
        <Text
          strong
          className="chart-total-value"
          style={isRestricted ? { fontWeight: 400 } : undefined}
        >
          {!isRestricted && block.tooltip ? (
            <Tooltip title={block.tooltip}>{valueNode}</Tooltip>
          ) : (
            valueNode
          )}
        </Text>
        {!isRestricted &&
          block.comparisonValue != null &&
          block.comparisonLabel && (
            <Text type="secondary" className="chart-total-comparison">
              {dataType === "rate"
                ? `${formatNumber(block.comparisonValue, 2, ",", ".")}%`
                : formatNumber(block.comparisonValue, 0, ",", ".")}{" "}
              {block.comparisonLabel}
            </Text>
          )}
      </Flex>
    </Flex>
  );
};

export default MCChartBlock;
